import React, {useMemo} from 'react';
import Box from '@mui/material/Box';
import {Editor} from '../../../../components/prosemirror-editor';
import MarginRail from '../../../../components/prosemirror-editor/components/MarginRail';
import useReportEditorCore from './hooks/useReportEditorCore';
import useImportedContentBridge from './hooks/useImportedContentBridge';
import useMarginRailController from './hooks/useMarginRailController';
import useSnippetDropConfig from './hooks/useSnippetDropConfig';
import {trackReportOpen, trackReportClose} from '../../../../hooks/useActivityTracking';
import './reportEditor.scss';

const EDITOR_BOX_SX = {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    overflowY: 'auto',
    bgcolor: 'white',
    position: 'relative',
};

export default function ReportEditor({
    containerClassName = 'report-editor-container',
    documentHeader,
    enableImportedContentBridge = true,
}) {
    const {
        reportId,
        user,
        editorView,
        websocketUrl,
        handleEditorReady,
        handleSelectionChange,
    } = useReportEditorCore();

    useImportedContentBridge({editorView, reportId, enabled: enableImportedContentBridge});

    // Measure time spent writing each report: report_open on mount / report_close on unmount.
    React.useEffect(() => {
        if (!reportId) return undefined;
        const openedAt = Date.now();
        trackReportOpen(reportId);
        return () => {
            trackReportClose(reportId, Date.now() - openedAt);
        };
    }, [reportId]);

    const {
        markers,
        active,
        activeReferenceUid,
        handleMarkerClick,
        handleMarkerDelete,
        handleThreadSnapshot,
    } = useMarginRailController(editorView, reportId);

    const snippetDropConfig = useSnippetDropConfig(reportId);

    const marginRail = useMemo(() => (
        <MarginRail
            markers={markers}
            active={active}
            activeReferenceUid={activeReferenceUid}
            onMarkerClick={handleMarkerClick}
            onMarkerDelete={handleMarkerDelete}
            onThreadSnapshot={handleThreadSnapshot}
            view={editorView}
            reportId={reportId}
        />
    ), [markers, active, activeReferenceUid, reportId, handleMarkerClick, handleMarkerDelete, handleThreadSnapshot, editorView]);

    return (
        <Box sx={EDITOR_BOX_SX} className={containerClassName}>
            <Editor
                documentId={reportId}
                user={user}
                websocketUrl={websocketUrl}
                showToolbar={true}
                showMetaMarksToolbar={true}
                onReady={handleEditorReady}
                onSelectionChange={handleSelectionChange}
                snippetDrop={snippetDropConfig}
                documentHeader={documentHeader}
                marginRail={marginRail}
            />
        </Box>
    );
}
